import { useCallback } from 'react';
import { usePageContext } from './page-context';
import { EICMSeverity, EICMType } from '../enums/icm-enum';
import { useTrackICMEvent, useBuildICMProperties } from '../services/logging/hooks';

const UNDEFINED_DISPLAY_DATE = 'Undefined';

const ISO_DATESTRING_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(\.\d+)?(Z|[+-][01]\d:[0-5]\d)?$/;
const TIMEZONE_SUFFIX_REGEX = /(Z|[+-][01]\d:[0-5]\d)$/;
const UTC_TIMEZONE_SUFFIX_REGEX = /Z$/;

const useTrackErrorTimezoneInvalid = (dataSource: string = ''): ((datestr: string) => void) => {
  const pageName = usePageContext();
  const trackICMEvent = useTrackICMEvent();
  const buildICMProperties = useBuildICMProperties();
  return useCallback(
    (datestr: string) => {
      const sharedErrorMessage = `API sent non UTC time without ISO formatting: ${datestr}`;
      const errorMessage = dataSource ? `[${dataSource}] ${sharedErrorMessage}` : sharedErrorMessage;
      const icmProps = buildICMProperties(EICMType.General, EICMSeverity.Sev4);
      trackICMEvent(errorMessage, pageName, dataSource, icmProps);
    },
    [buildICMProperties, dataSource, pageName, trackICMEvent]
  );
};

const useTrackErrorDateFormatInvalid = (dataSource: string = ''): ((datestr: string) => void) => {
  const pageName = usePageContext();
  const trackICMEvent = useTrackICMEvent();
  const buildICMProperties = useBuildICMProperties();
  return useCallback(
    (datestr: string) => {
      const sharedErrorMessage = 'Attempting to parse invalid date format';
      const errorMessage = dataSource ? `[${dataSource}] ${sharedErrorMessage}` : sharedErrorMessage;
      const icmProps = {
        ...buildICMProperties(EICMType.General, EICMSeverity.Sev4),
        invalidDateValue: datestr,
      };
      trackICMEvent(errorMessage, pageName, dataSource, icmProps);
    },
    [buildICMProperties, dataSource, pageName, trackICMEvent]
  );
};

export const useUtcDateParse = (dataSource: string = ''): ((date: Date | string | null | undefined) => Date | undefined) => {
  const trackDateFormatInvalid = useTrackErrorDateFormatInvalid(dataSource);
  const trackInvalidTimezone = useTrackErrorTimezoneInvalid(dataSource);

  return useCallback(
    (date: Date | string | null | undefined): Date | undefined => {
      if (date === null || date === undefined || date === '') {
        return undefined;
      }

      if (typeof date !== 'string') {
        return date;
      }

      if (!ISO_DATESTRING_REGEX.test(date)) {
        trackDateFormatInvalid(date);
        return undefined;
      }

      if (!UTC_TIMEZONE_SUFFIX_REGEX.test(date)) {
        trackInvalidTimezone(date);

        if (TIMEZONE_SUFFIX_REGEX.test(date)) {
          return new Date(date);
        }

        const reformattedDate = `${date}Z`;

        // should always be valid - this check is for superstition
        if (!ISO_DATESTRING_REGEX.test(reformattedDate)) {
          trackDateFormatInvalid(date);
          return undefined;
        }

        return new Date(reformattedDate);
      }

      return new Date(date);
    },
    [trackDateFormatInvalid, trackInvalidTimezone]
  );
};

/**
 * Returns the browser's local IANA timezone, falling back to 'UTC' if the
 * resolved timezone is invalid (e.g. "Etc/Unknown" on misconfigured devices).
 * Passing an unrecognised timezone to toLocaleDateString throws a RangeError.
 */
const getSafeLocalTimeZone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Validate by constructing a formatter with the resolved timezone.
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return 'UTC';
  }
};

export const useMonthDayYear = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'utc',
      });
    },
    [utcDateParse]
  );
};

export const useMonthDayYearWithDefaultTimeZone = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    [utcDateParse]
  );
};

export const useMonthDayYearTimeWithDefaultTimeZone = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: getSafeLocalTimeZone(),
        timeZoneName: 'short',
      });
    },
    [utcDateParse]
  );
};

export const useMonthDayYearTimeAndZoneWithDefaultTimeZone = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hourCycle: 'h12',
        hour: '2-digit',
        hour12: true,
        minute: '2-digit',
        timeZoneName: 'short',
        timeZone: getSafeLocalTimeZone(),
      });
    },
    [utcDateParse]
  );
};

export const useMonthDayYearAndTimeUTC = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hourCycle: 'h12',
        hour: '2-digit',
        hour12: true,
        minute: '2-digit',
        timeZoneName: 'short',
        timeZone: 'utc',
      });
    },
    [utcDateParse]
  );
};

export const useMonthAndDay = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'utc',
      });
    },
    [utcDateParse]
  );
};

export const useMonthAndDayWithDefaultTimeZone = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: getSafeLocalTimeZone(),
      });
    },
    [utcDateParse]
  );
};

export const useMonth = (): ((date?: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date?: Date | string): string => {
      const utcDate = utcDateParse(date || '');
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'long',
        timeZone: 'utc',
      });
    },
    [utcDateParse]
  );
};

export const useYear = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        year: 'numeric',
        timeZone: 'utc',
      });
    },
    [utcDateParse]
  );
};

export const useMonthAndYear = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'utc',
      });
    },
    [utcDateParse]
  );
};

export const useMonthNumber = (): ((date: Date | string) => string) => {
  const utcDateParse = useUtcDateParse();
  return useCallback(
    (date: Date | string): string => {
      const utcDate = utcDateParse(date);
      if (utcDate === undefined) {
        return UNDEFINED_DISPLAY_DATE;
      }
      return utcDate.toLocaleDateString('en-US', {
        month: 'numeric',
        timeZone: 'utc',
      });
    },
    [utcDateParse]
  );
};

const monthAbbreviations: { [key: string]: string } = {
  January: 'Jan',
  February: 'Feb',
  March: 'Mar',
  April: 'Apr',
  May: 'May',
  June: 'Jun',
  July: 'Jul',
  August: 'Aug',
  September: 'Sep',
  October: 'Oct',
  November: 'Nov',
  December: 'Dec',
};

const monthNameToFiscalMonthNumMap: { [key: string]: number } = {
  January: 7,
  February: 8,
  March: 9,
  April: 10,
  May: 11,
  June: 12,
  July: 1,
  August: 2,
  September: 3,
  October: 4,
  November: 5,
  December: 6,
};

export const monthNameToAbbr = (monthName: string): string => monthAbbreviations[monthName] || UNDEFINED_DISPLAY_DATE;

export const monthNameToFiscalMonthNumber = (monthName: string): number => monthNameToFiscalMonthNumMap[monthName] || 0;

const isLeapYear = (yearVal: number) => yearVal % 400 === 0 || (yearVal % 100 !== 0 && yearVal % 4 === 0);

export const getTotalDaysInYear = (yearVal: number) => (isLeapYear(yearVal) ? 366 : 365);

const fiscalMonthtoMonth: { [key: string]: string } = {
  M1: 'July',
  M2: 'August',
  M3: 'September',
  M4: 'October',
  M5: 'November',
  M6: 'December',
  M7: 'January',
  M8: 'Febuary',
  M9: 'March',
  M10: 'April',
  M11: 'May',
  M12: 'June',
};

export const getMonthFromFiscalCode = (fiscalCode: string = '') => {
  const fiscalMonth = (fiscalCode.match(/M\d+/g) || [])[0] || '';
  return fiscalMonthtoMonth[fiscalMonth];
};

export const useParseMonth = (): ((date: string) => string) => {
  const trackDateFormatInvalid = useTrackErrorDateFormatInvalid();

  return useCallback(
    (date: string): string => {
      if (!date) {
        trackDateFormatInvalid(date);
        return UNDEFINED_DISPLAY_DATE;
      }
      const month = date.trim().split(/(,|\s)/)[0];

      if (Object.keys(monthAbbreviations).indexOf(month) === -1) {
        trackDateFormatInvalid(date);
        return UNDEFINED_DISPLAY_DATE;
      }

      return month;
    },
    [trackDateFormatInvalid]
  );
};

export const currentDateInPST = () => {
  const date = new Date();
  const options = { timeZone: 'America/Los_Angeles' };
  const pstDate = date.toLocaleString('en-US', options);
  return new Date(pstDate);
};

export const useIsSameDay = () =>
  useCallback((date1: Date | string, date2: Date | string): boolean => {
    const dateA = typeof date1 === 'string' ? new Date(date1) : date1;
    const dateB = typeof date2 === 'string' ? new Date(date2) : date2;

    if (Number.isNaN(dateA.getTime()) || Number.isNaN(dateB.getTime())) {
      return false;
    }

    return dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth() && dateA.getDate() === dateB.getDate();
  }, []);

// Formats 'Month YYYY' as 'Month, YYYY', else returns as is
export const getFormattedMonthYear = (title: string) => {
  const match = title.match(/^(\w+)(\s+\d{4})$/);
  if (match) {
    return `${match[1]},${match[2]}`;
  }
  return title;
};

/**
 * Compares two dates by date only (ignoring time components).
 * Returns -1 if date1 is before date2, 0 if they are the same date, 1 if date1 is after date2.
 *
 * @param date1 - First date to compare (Date object or ISO string)
 * @param date2 - Second date to compare (Date object or ISO string)
 * @returns -1 if date1 < date2, 0 if date1 === date2, 1 if date1 > date2
 */
export const compareDatesOnly = (date1: Date | string, date2: Date | string): number => {
  // Extract date part from ISO strings to avoid timezone issues
  const getDateString = (date: Date | string): string => {
    if (typeof date === 'string') {
      // For ISO strings, extract everything before 'T' (date part only)
      return date.includes('T') ? date.split('T')[0] : date;
    }
    // For Date objects, format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateStr1 = getDateString(date1);
  const dateStr2 = getDateString(date2);

  // Handle invalid date strings
  if (!dateStr1 || !dateStr2 || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr1) || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr2)) {
    return 0; // Treat invalid dates as equal
  }

  // Simple string comparison for YYYY-MM-DD format
  if (dateStr1 < dateStr2) return -1;
  if (dateStr1 > dateStr2) return 1;
  return 0;
};

/**
 * Returns the estimation cutoff: the 15th of the month after the quarter end date (inclusive).
 * The date part is extracted first to avoid timezone drift.
 */
export const getEstimationGracePeriodEnd = (quarterEndDate: Date | string): Date => {
  let year: number;
  let month: number; // 0-indexed
  if (typeof quarterEndDate === 'string') {
    const datePart = quarterEndDate.includes('T') ? quarterEndDate.split('T')[0] : quarterEndDate;
    const [parsedYear, parsedMonth] = datePart.split('-').map(Number);
    year = parsedYear;
    month = parsedMonth - 1;
  } else {
    year = quarterEndDate.getFullYear();
    month = quarterEndDate.getMonth();
  }
  return new Date(year, month + 1, 15);
};

/**
 * Hook that returns a function to compare two dates by date only (ignoring time components).
 * Useful when you need to compare dates in a React component context.
 *
 * @returns A function that compares two dates and returns -1, 0, or 1
 */
export const useCompareDatesOnly = () =>
  useCallback((date1: Date | string, date2: Date | string): number => {
    return compareDatesOnly(date1, date2);
  }, []);
